import http, { BASE_URL_2, BASE_URL_3, HttpSuccessOrErrorType, OkPacket, ResponseErrorType } from '../../http';
import { Section } from '../../../models/section.entity';
import { Customer } from '../../../models/customer.entity';
import { QopCreateOrder } from '../../../models/order-head.entity';
import { BannerAnnouncement, Bulletin, BusinessHour2, PostBusinessHour2Params, Propaganda, Country, Restaurant } from '../../../models';
import { CallServiceContent } from '../../../page/rbp-cloud-sub-function/call-service.page';
import { SharkImage } from '../../../models/type.constant';
import { SectionTable } from '../../../models/section-table.entity';
import { getRBPStringId } from '../../../page/main/main-page-states';

/**
 * 餐厅相关Api
 */
const restaurantApi = {

    /**
     * 获取桌台
     * @param params  with_section - 是否按区域分类
     * @param params  with_order - 是否获取对应的订单信息
     * @param params  section_id - 区域id
     * @param params  menu_id - shark_setting中package-fee的menu_id
     */
    async get_res_tables(params: { with_section?: boolean, with_order?: boolean, section_id?: number, menu_id?: string }): Promise<any> {
        return await http.get(`${BASE_URL_3}/tables/${getRBPStringId()}`, { params })
    },

    /**
     * 根据桌台id获取桌台详情
     * @param tableId
     * @returns
     */
    async get_tables_id(tableId: number): Promise<SectionTable[]> {
        return await http.get(`${BASE_URL_3}/tables/${tableId}/${getRBPStringId()}`)
    },
    /**
     * 根据桌台id修改桌台状态
     * @param tableId
     * @param data
     * @returns
     */
    async patch_tables_id(tableId: number, data: SectionTable): Promise<OkPacket> {
        return await http.patch(`${BASE_URL_3}/tables/${tableId}/${getRBPStringId()}`, data)
    },

    /**
     * 创建订单不检查桌台状态
     * @param data
     * @returns
     */
    async post_order_not_check_table_status(data: QopCreateOrder): Promise<{
        // 单号
        check_num: number;
        // 是否成功
        ok: true
        // 订单id
        orderId: number;
        // 桌台名称
        table_name: string
    }> {
        return await http.post(`${BASE_URL_3}/orders/not-check-table-status/${getRBPStringId()}`, data)
    },

    /**
     *
     * @param extraCostId
     * @param data
     * @returns
     */
    async post_gen_extra_dish(extraCostId: number, data: {
        // 订单id
        id: number,
        // 1级菜单id,数组
        first_menu: Array<number>,
        // 额外费用菜单id
        menu_id: number,
        // 操作人员id
        open_employee_id: number,
        // 操作人员名称
        open_employee_name: number,
        // 星期 0-6 0 星期天
        week: number;
    }): Promise<any> {
        return await http.post(`${BASE_URL_3}/orders/${data.id}/gen-extra-dish/${getRBPStringId()}`, data)
    },

    /**
     * 修改订单临时密码 （QOP）
     * @param data
     * @returns
     */
    async patch_order_password(data: {
        employee_id: string,
        password_id: string,
        order_head_id: number,
        new_order_code: string,
    }): Promise<OkPacket> {
        return await http.patch(`${BASE_URL_3}/order-password/update/${getRBPStringId()}`, data)
    },
    /**
     * 创建桌台
     */
    async post_res_table(params: SectionTable): Promise<any> {
        return await http.post(`${BASE_URL_3}/table/${getRBPStringId()}`, params)
    },

    /**
     * 批量创建桌台
     */
    async post_mul_res_table(params: SectionTable & { count: number }): Promise<any> {
        return await http.post(`${BASE_URL_3}/table/batch/${getRBPStringId()}`, params)
    },

    /**
     * 获取顾客
     */
    async get_customer(params: { customer_id?: number; customer_name?: string; telephone_1?: string }): Promise<HttpSuccessOrErrorType<any>> {
        return await http.get(`${BASE_URL_3}/customers/${getRBPStringId()}`, {
            params
        })
    },

    /**
     * 批量删除顾客
     */
    async del_customer(ids: number[]): Promise<HttpSuccessOrErrorType<any>> {
        return await http.delete(`${BASE_URL_3}/customers/${getRBPStringId()}`, {
            data: {
                ids
            }
        })
    },

    /**
     * 单个删除顾客
     */
    async del_one_customer(id: number): Promise<HttpSuccessOrErrorType<any>> {
        return await http.delete(`${BASE_URL_3}/customers/${id}/${getRBPStringId()}`)
    },

    /**
     * 批量删除顾客
     */
    async batch_del_customer(ids: number[]): Promise<HttpSuccessOrErrorType<any>> {
        return await http.delete(`${BASE_URL_3}/customers/${getRBPStringId()}`, { data: { ids } })
    },

    /**
     * 修改顾客信息
     */
    async patch_customer(details: Customer): Promise<HttpSuccessOrErrorType<any>> {
        return await http.patch(`${BASE_URL_3}/customers/${details.customer_id}/${getRBPStringId()}`, {
            ...details
        })
    },
    /**
     * 编辑桌台
     */
    async patch_res_table(table_id: number, params: Partial<SectionTable>): Promise<any> {
        return await http.patch(`${BASE_URL_3}/tables/${table_id}/${getRBPStringId()}`, params)
    },
    /**
     * 删除桌台
     */
    async delete_res_table(table_id: number): Promise<any> {
        return await http.delete(`${BASE_URL_3}/table/${table_id}/${getRBPStringId()}`)
    },
    /**
     * 批量删除桌台
     */
    async delete_res_table_batch(params: { ids: number[] }): Promise<any> {
        return await http.delete(`${BASE_URL_3}/table/${getRBPStringId()}`, {
            data: params
        })
    },
    /**
     * 创建区域
     */
    async post_res_area(params: Section): Promise<any> {
        return await http.post(`${BASE_URL_3}/rvcCenter/${getRBPStringId()}`, params)
    },
    /**
     * 删除桌台
     */
    async delete_res_area(table_id: number): Promise<any> {
        return await http.delete(`${BASE_URL_3}/rvcCenter/${table_id}/${getRBPStringId()}`)
    },
    /**
     * 编辑区域
     */
    async patch_res_area(area_id: number, params: Partial<Section>): Promise<any> {
        return await http.patch(`${BASE_URL_3}/rvcCenter/${area_id}/${getRBPStringId()}`, params)
    },
    /**
     * excel桌台   excel批量上传桌台
     */
    async post_excel_table(data: {}): Promise<any> {
        return await http.post(`${BASE_URL_3}/table/excel/${getRBPStringId()}`, { data })
    },

    /**
     * 创建一个呼叫服务
     */
    async post_call_service(details: CallServiceContent): Promise<CallServiceContent> {
        return await http.post(`${BASE_URL_2}/callservice/${getRBPStringId()}`, {
            ...details
        })
    },

    /**
     * 获取呼叫服务
     */
    async get_call_service(): Promise<CallServiceContent[]> {
        return await http.get(`${BASE_URL_2}/callservice/${getRBPStringId()}`)
    },

    /**
     * 删除单个呼叫服务
     */
    async del_call_service(id: number): Promise<any> {
        return await http.delete(`${BASE_URL_2}/callservice/${id}/${getRBPStringId()}`)
    },

    // 批量删除呼叫服务
    async patch_del_cell_service(ids: number[]): Promise<any> {
        return await http.delete(`${BASE_URL_2}/callservice/${getRBPStringId()}`, {
            data: {
                ids
            }
        });
    },

    /**
     * 修改一个呼叫服务
     */
    async patch_call_service(details: any): Promise<any> {
        return await http.patch(`${BASE_URL_2}/callservice/${details.id}/${getRBPStringId()}`, {
            ...details
        })
    },

    /**
     * 获取餐厅的特色菜
     */
    async get_special_dishes(): Promise<any> {
        return await http.get(`${BASE_URL_2}/special_dishes/${getRBPStringId()}`)
    },

    /**
     * 删除特色菜
     */
    async del_special_dishes(id: string): Promise<any> {
        return await http.delete(`${BASE_URL_3}/special_dishes/${id}/${getRBPStringId()}`)
    },

    /**
     * 新增一个特色菜
     */
    async post_special_dishes(id: number): Promise<any> {
        return await http.post(`${BASE_URL_3}/special_dishes/${getRBPStringId()}`, {
            menu_item_id: id
        })
    },

    // 查询宣传资料
    async get_propaganda(): Promise<Propaganda[]> {
        return await http.get(`${BASE_URL_3}/propaganda/${getRBPStringId()}`)
    },
    /**
     * 新建宣传资料F
     * @param data
     * @returns
     */
    async post_propaganda(data: Propaganda): Promise<OkPacket> {
        return await http.post(`${BASE_URL_3}/propaganda/${getRBPStringId()}`, data)
    },
    /**
     * 修改宣传资料
     * @param id
     * @param data
     * @returns
     */
    async patch_propaganda(id: number, data: Propaganda): Promise<OkPacket> {
        return await http.patch(`${BASE_URL_3}/propaganda/${id}/${getRBPStringId()}`, data)
    },
    /**
     * 删除宣传资料
     * @param id
     * @returns
     */
    async delete_propaganda(id: number): Promise<OkPacket> {
        return await http.delete(`${BASE_URL_3}/propaganda/${id}/${getRBPStringId()}`)
    },

    /**
     * 查询顶部文字公告
     * @returns
     */
    async get_banner_announcement(): Promise<BannerAnnouncement[]> {
        return await http.get(`${BASE_URL_3}/banner-announcement/${getRBPStringId()}`)
    },
    /**
     * 新建顶部文字公告
     * @param data
     * @returns
     */
    async post_banner_announcement(data: BannerAnnouncement): Promise<OkPacket> {
        return await http.post(`${BASE_URL_3}/banner-announcement/${getRBPStringId()}`, data)
    },
    /**
     * 修改顶部文字公告
     * @param id
     * @param data
     * @returns
     */
    async patch_banner_announcement(id: number, data: BannerAnnouncement): Promise<OkPacket> {
        return await http.patch(`${BASE_URL_3}/banner-announcement/${id}/${getRBPStringId()}`, data)
    },
    /**
     *  删除顶部文字公告
     * @param id
     * @returns
     */
    async delete_banner_announcement(id: number): Promise<OkPacket> {
        return await http.delete(`${BASE_URL_3}/banner-announcement/${id}/${getRBPStringId()}`)
    },

    /**
     * 上传餐厅活动公告图片
     * @param id
     * @param file
     * @returns
     */
    async post_activity_bulletin_image(id: number, file: File): Promise<string> {
        const formData = new FormData();
        formData.append('name', `activity-bulletin${id}`);
        formData.append('image', file);
        return await http.post(`${BASE_URL_3}/activity-bulletin/image/${getRBPStringId()}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            }
        })

    },

    /**
     * 删除餐厅活动公告图片
     * @param id
     * @returns
     */
    async delete_activity_bulletin_image(id: number): Promise<string> {
        return await http.delete(`${BASE_URL_3}/activity-bulletin/image/activity-bulletin${id}/${getRBPStringId()}`)
    },

    /**
     * 上传图片就餐指南
     * @param id
     * @param file
     * @returns
     */
    async post_bulletin_image(id: number, file: File): Promise<string> {
        const formData = new FormData();
        formData.append('number', `${id}`);
        formData.append('image', file);
        return await http.post(`${BASE_URL_3}/bulletin/image/${getRBPStringId()}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            }
        })

    },

    /**
     * 删除图片就餐指南
     * @param id
     * @returns
     */
    async delete_bulletin_image(id: number): Promise<string> {
        return await http.delete(`${BASE_URL_3}/bulletin/image/${id}/${getRBPStringId()}`)
    },

    // 获取文字就餐指南
    async get_bulletin(): Promise<Bulletin[]> {
        return await http.get(`${BASE_URL_2}/bulletin/4/${getRBPStringId()}`)
    },

    /**
     * 修改文字就餐指南
     * @param value
     * @returns
     */
    async patch_bulletin(value: string): Promise<OkPacket> {
        return await http.patch(`${BASE_URL_2}/bulletin/4/${getRBPStringId()}`, { value })
    },


    /**
     * 获取设置信息
     * @param id 设置的id
     */
    async get_setting(id: number): Promise<any> {
        return await http.get(`${BASE_URL_3}/settings/${id}/${getRBPStringId()}`)
    },


    /**
     * 修改设置信息
     * @param id 设置的id
     */
    async patch_setting(id: number, param: any): Promise<any> {
        return await http.patch(`${BASE_URL_3}/settings/${id}/${getRBPStringId()}`, param)
    },

    /**
     * 获取图片信息
     * @param img 图片
     */
    async get_setting_img(img: string): Promise<any> {
        return await http.get(`/${img}`)
    },

    /**
     * 修改设置图片信息
     * @param file
     * @returns
     */
    async post_background_img(file: File): Promise<any> {
        const formData = new FormData();
        formData.append('image', file);
        return await http.post(`${BASE_URL_3}/background/${getRBPStringId()}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            }
        })
    },

    /**
     * 修改菜单背景图片信息
     * @param file
     * @returns
     */
    async post_menu_background_img(file: File): Promise<any> {
        const formData = new FormData();
        formData.append('image', file);
        return await http.post(`${BASE_URL_3}/menu/background/${getRBPStringId()}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            }
        })
    },

    /**
     * 删除菜单背景图片
     * @returns
     */
    async delete_menu_background_img(): Promise<any> {
        return await http.delete(`${BASE_URL_3}/menu/bg/menu_bg/${getRBPStringId()}`)
    },

    /**
     * 获取营业时间设置
     */
    async get_set_business_hour(): Promise<any> {
        return await http.get(`${BASE_URL_3}/set_business_hour2/${getRBPStringId()}`)
    },

    /**
     * 修改营业时间设置
     */
    async patch_set_business_hour(param: { set_business_hour: string }): Promise<any> {
        return await http.patch(`${BASE_URL_3}/set_business_hour2/${getRBPStringId()}`, param)
    },

    /**
     * 获取一级菜单
     */
    async get_first_menu(params: { type: number, with_dish: boolean }): Promise<any> {
        return await http.get(`${BASE_URL_3}/menus/${getRBPStringId()}`, { params })
    },

    /**
     * 获取营业时间
     */
    async get_v2_business_hour(): Promise<any> {
        return await http.get(`${BASE_URL_3}/business-hour/${getRBPStringId()}`)
    },

    /**
     * 删除一个营业时间数据
     *
     */
    async del_business_hour(id: number): Promise<any> {
        return await http.delete(`${BASE_URL_3}/business-hour/${id}/${getRBPStringId()}`)
    },

    /**
     * 新增一个营业时间数据
     */
    async post_v2_business_hour(params: {
        alternative_menu: string;
        end_time: string;
        preferred_menu: string;
        start_time: string;
    }): Promise<any> {
        return await http.post(`${BASE_URL_3}/business-hour/${getRBPStringId()}`, params)
    },

    /**
     * 编辑一个营业时间数据
     */
    async patch_v2_business_hour(id: number, params: {
        alternative_menu: string;
        end_time: string;
        preferred_menu: string;
        start_time: string;
    }): Promise<any> {
        return await http.patch(`${BASE_URL_3}/business-hour/${id}/${getRBPStringId()}`, params)
    },
    // 获取营业时间
    async get_business_hour2(): Promise<BusinessHour2[]> {
        return await http.get(`${BASE_URL_3}/business_hour2/${getRBPStringId()}`)
    },
    /**
     * 修改营业时间
     * @param data
     * @returns
     */
    async post_business_hour2(data: PostBusinessHour2Params[]): Promise<OkPacket[]> {
        return await http.post(`${BASE_URL_3}/business_hour2/${getRBPStringId()}`, data)
    },

    /**
     * 清空营业数据
     * @returns
     */
    async delete_business(): Promise<OkPacket> {
        return await http.delete(`${BASE_URL_3}/business/${getRBPStringId()}`)
    },

    /**
     * 获取餐馆信息
     * @returns
     */
    async get_restaurant_info(): Promise<Restaurant> {
        return await http.get(`${BASE_URL_3}/restaurant-info/${getRBPStringId()}`)
    },
    /**
     * 按指定餐厅码获取餐馆信息（菜单库模块内选店等与顶栏当前店分离的场景）
     * @param restaurantStringId - RBP `string_id`
     */
    async get_restaurant_info_by_string_id(restaurantStringId: string): Promise<Restaurant> {
        const sid = (restaurantStringId && restaurantStringId.trim()) || '';
        return await http.get(`${BASE_URL_3}/restaurant-info/${encodeURIComponent(sid)}`);
    },
    /**
     * 修改餐馆信息
     * @returns
     */
    async patch_restaurant_info(data: Restaurant & {
        // 修改“国家和地区”时传
        ccTLD?: string;
        country_code?: string;
        country_id?: number;
    }): Promise<OkPacket> {
        return await http.patch(`${BASE_URL_3}/restaurant-info/${data.restaurant_id}/${getRBPStringId()}`, data)
    },
    /**
     * 获取国家列表信息
     * @returns
     */
    async get_country(): Promise<HttpSuccessOrErrorType<Country[]>> {
        return await http.get(`${BASE_URL_3}/country`)
    },
    /**
     * 修改餐厅logo
     * @returns
     */
    async post_qop_logo(imageFile: File): Promise<string> {
        const formData = new FormData();
        formData.append('image', imageFile);
        return await http.post(`${BASE_URL_3}/qop/logo/${getRBPStringId()}`, formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
    },

    /**
     * 移动桌台的下标，桌台的id会变化
     */
    async patch_table_sort(originIndex: number, targetIndex: number): Promise<OkPacket & ResponseErrorType> {
        return await http.patch(`${BASE_URL_3}/table/sort/${getRBPStringId()}`, {
            origin_sort: originIndex,
            target_sort: targetIndex
        })
    },

    /**
     * 新增桌台
     */
    async post_res_table_subscript(params: SectionTable): Promise<any> {
        return await http.post(`${BASE_URL_3}/table_plugin/${getRBPStringId()}`, params)
    },

    /**
     * 移动桌台区域
     */
    async patch_table_area(ids: number[], rvc_center_id: number): Promise<OkPacket & ResponseErrorType> {
        return await http.patch(`${BASE_URL_3}/table_rvc_center/${getRBPStringId()}`, {
            ids,
            rvc_center_id
        })
    },

    /**
     * 获取shark的300k以上图片
     */
    async get_shark_images(): Promise<SharkImage[]> {
        return await http.get(`${BASE_URL_3}/scan/large_images/${getRBPStringId()}`)
    },

    /**
     * 更换shark的图片
     */
    async post_shark_images(params: { file: File, path: string }[]): Promise<string> {
        const formData = new FormData();
        params.forEach(item => {
            formData.append(`${item.path}`, item.file);
        })
        return await http.post(`${BASE_URL_3}/override/dir_large_images/${getRBPStringId()}`, formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
    },

    /**
     * 创建/批量创建桌牌（与 RBP v3 `create_table_markers` 一致，餐厅码挂路径）
     */
    async create_table_markers(params: {
        table_marker_num: number;
        isBatch?: boolean;
        count?: number;
    }): Promise<any> {
        return await http.post(`${BASE_URL_3}/create_table_markers/${getRBPStringId()}`, params);
    },

    /**
     * 获取桌牌列表
     */
    async get_table_markers(table_marker_num?: number): Promise<any> {
        return await http.get(`${BASE_URL_3}/get_table_markers/${getRBPStringId()}`, {
            params: table_marker_num != null ? { table_marker_num } : {},
        });
    },

    /**
     * 批量删除桌牌
     */
    async delete_table_markers(ids: number[]): Promise<any> {
        return await http.delete(`${BASE_URL_3}/delete_table_markers/${getRBPStringId()}`, {
            data: { ids },
        });
    },

}
export default restaurantApi;